import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Icon, SubModal, SplitButton,
  Grid, FieldText, FieldSelect, FieldTextarea,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

// Liste-only RuleCategoryPage'in modal-edit'inden TAM-SAYFA detaya taşınan ana kayıt.
// AYNI REST uçlarını kullanır: POST /api/RuleCategories, PUT /{id}, DELETE /{id} (Add/Update suffix YOK).
// GetById ucu yok → kayıt önce navigation state'ten (row); yoksa GetAll listesinden id ile bulunur.
const API_BASE = '/api/RuleCategories';

const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];
const EMPTY_FORM = { name: '', description: '', isActive: 'true' };

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function rowToForm(r) {
  return {
    name: pick(r, 'name', 'Name') ?? '',
    description: pick(r, 'description', 'Description') ?? '',
    isActive: (pick(r, 'isActive', 'IsActive') ?? false) ? 'true' : 'false',
  };
}

export function RuleCategoryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Kural Kategorisi' : 'Kural Kategorisi Detayı', 'Kural kategorisi kaydını oluşturun veya düzenleyin.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // Tek kayıt yükleme: önce nav state'teki row; yoksa GetAll listesinden id ile bul (GetById ucu yok).
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    if (location.state?.row) {
      setForm(rowToForm(location.state.row));
      // Nav-state satırı liste OData $select'inden gelir → audit alanları eksik.
      // GetById ucu yok; audit'i GetAll listesinden id ile NON-BLOCKING çek.
      fetch(API_BASE, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          const items = unwrap(body);
          const rec = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
          if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(API_BASE, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Kayıt alınamadı (${res.status})`);
      const items = unwrap(await res.json());
      const found = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
      if (!found) throw new Error('Kayıt bulunamadı');
      setForm(rowToForm(found));
      setAudit({ createdBy: pick(found, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(found, 'createdDate', 'CreatedDate'), updatedBy: pick(found, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(found, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Kayıt alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, location.state]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /rulecategories/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  // location.key her navigasyonda değiştiği için bir klon yalnızca bir kez uygulanır.
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildPayload = () => ({
    name: form.name.trim(),
    description: form.description,
    isActive: form.isActive === 'true',
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp yeni boş kayda hazırlan
  // (önceki kayıt backend'e kaydedilmiş olur). Sadece yeni-kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.name.trim()) { showToast('error', 'Kategori adı zorunludur'); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await fetch(isNew ? API_BASE : `${API_BASE}/${id}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id: Number(id), ...payload }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || (isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'Kural kategorisi eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Kural kategorisi eklendi.');
        setTimeout(() => navigate('/rulecategories'), 400);
        return;
      }
      showToast('success', 'Kural kategorisi güncellendi.');
      setTimeout(() => navigate('/rulecategories'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın tüm form bilgilerini taşıyarak yeni-kayıt ekranını aç.
  // Hiçbir şey kaydedilmez; kullanıcı "Kaydet" diyene kadar yeni kayıt oluşmaz.
  const handleClone = () => {
    navigate('/rulecategories/new', { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Kayıt silinemedi');
      showToast('success', 'Kural kategorisi silindi.');
      setTimeout(() => navigate('/rulecategories'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const statusBadge = !isNew && (form.isActive === 'true'
    ? <StatusBadge tone="ok">Aktif</StatusBadge>
    : <StatusBadge tone="neutral">Pasif</StatusBadge>);

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/rulecategories')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni kategori aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Kural Kategorileri', to: '/rulecategories' }, { label: isNew ? 'Yeni Kural Kategorisi' : 'Kural Kategorisi Detayı', current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'RuleCategory', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="layers" title="Kural Kategorisi Bilgileri" subtitle="Kategori adı, açıklaması ve durumu">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="name" label="Kategori Adı" req span />
            <FieldSelect form={form} set={set} k="isActive" label="Durum" options={ACTIVE_OPTIONS} />
            <FieldTextarea form={form} set={set} k="description" label="Açıklama" rows={3} span />
          </Grid>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.name}”</strong> kural kategorisini silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default RuleCategoryDetailPage;
